import React, { useEffect, useState } from 'react';
import { payrunApi } from '../api/client';
import type { PastRun, PastRunLineResult, XeroTenant } from '../types';

interface PastRunsProps {
  reloadKey: number;
  xeroTenants: XeroTenant[];
}

function fmtMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '$0.00';
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(n)) return String(value);
  return `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return s;
  }
}

function fmtDateTime(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return s;
  }
}

export default function PastRuns({ reloadKey, xeroTenants }: PastRunsProps) {
  // Live tenant-name lookup so cards always show a real org name, even when
  // an old past-run row was saved with a null tenantName snapshot.
  const tenantNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const t of xeroTenants) map.set(t.tenantId, t.tenantName);
    return map;
  }, [xeroTenants]);
  const displayName = (run: PastRun): string =>
    run.tenantName ?? tenantNameById.get(run.tenantId) ?? run.tenantId;
  const [runs, setRuns] = useState<PastRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PastRun | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await payrunApi.list();
      setRuns(data.payruns);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to load past runs';
      setError(msg);
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await payrunApi.delete(pendingDelete.id);
      setPendingDelete(null);
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to delete past run';
      setDeleteError(msg);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="flex-1 px-6 py-8 lg:px-10 max-w-6xl mx-auto w-full">
      <div className="mb-8 border-b border-slate-200 pb-6">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Contractor super</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">Past Runs</h1>
        <p className="mt-1 text-sm text-slate-500">
          Every pay run you've created from this app. Items are kept even after a Xero organisation is
          disconnected, and reappear when you reconnect.
        </p>
      </div>

      {loading && (
        <div className="rounded-lg border border-slate-200 bg-white px-5 py-6 text-sm text-slate-500">
          Loading past runs…
        </div>
      )}

      {!loading && error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {!loading && !error && runs && runs.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white px-5 py-8 text-center">
          <p className="text-sm font-medium text-slate-700">No past runs yet</p>
          <p className="mt-1 text-xs text-slate-500">
            Once you create a pay run from the Import Report page, it will appear here.
          </p>
        </div>
      )}

      {!loading && !error && runs && runs.length > 0 && (
        <ul className="space-y-3">
          {runs.map((run) => {
            const isOpen = expandedId === run.id;
            return (
              <li
                key={run.id}
                className="overflow-hidden rounded-lg border border-slate-200 bg-white"
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setExpandedId(isOpen ? null : run.id)}
                    className="flex flex-1 items-center gap-3 text-left min-w-0"
                  >
                    <ChevronIcon open={isOpen} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="text-sm font-semibold text-slate-900 truncate">
                          {fmtDate(run.payPeriodStartDate)} – {fmtDate(run.payPeriodEndDate)}
                        </span>
                        <StatusBadge run={run} />
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                        <span>Created {fmtDateTime(run.createdAt)}</span>
                        <span>•</span>
                        <span className="font-medium text-slate-700">{fmtMoney(run.totalSuper)} super</span>
                        <span>•</span>
                        <span className="truncate">{displayName(run)}</span>
                      </div>
                    </div>
                  </button>

                  <div className="flex flex-shrink-0 items-center gap-1">
                    {run.hasSourceFile && (
                      <a
                        href={payrunApi.sourceFileUrl(run.id)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        title={`Download original report${run.sourceFilename ? ` (${run.sourceFilename})` : ''}`}
                      >
                        <DownloadIcon />
                        Source
                      </a>
                    )}
                    <a
                      href={payrunApi.csvUrl(run.id)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      title="Download as CSV"
                    >
                      <DownloadIcon />
                      CSV
                    </a>
                    <a
                      href={payrunApi.pdfUrl(run.id)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                      title="Download formatted PDF report"
                    >
                      <PdfIcon />
                      PDF
                    </a>
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteError(null);
                        setPendingDelete(run);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50"
                      title="Delete past run"
                    >
                      <TrashIcon />
                      Delete
                    </button>
                  </div>
                </div>

                {isOpen && <RunDetails run={run} />}
              </li>
            );
          })}
        </ul>
      )}

      {pendingDelete && (
        <DeleteModal
          run={pendingDelete}
          isSubmitting={deleting}
          errorMessage={deleteError}
          onCancel={() => {
            if (deleting) return;
            setPendingDelete(null);
            setDeleteError(null);
          }}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
}

function StatusBadge({ run }: { run: PastRun }) {
  const failed = run.success === 'false';
  const cls = failed
    ? 'bg-amber-100 text-amber-800'
    : 'bg-emerald-100 text-emerald-800';
  const label = (failed ? `${run.status} • warnings` : run.status) + (run.finalisedAt ? ' • finalised' : '');
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {label}
    </span>
  );
}

function RunDetails({ run }: { run: PastRun }) {
  const lines = run.lineResults ?? [];
  const quickSuperLines = run.quickSuperLines ?? [];
  const hasQuickSuper = run.quickSuperLines != null;
  return (
    <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-4 space-y-4">
      <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        <DetailRow label="Created at" value={fmtDateTime(run.createdAt)} />
        <DetailRow label="Xero PayRun ID" value={run.xeroPayRunId} mono />
        <DetailRow label="Status" value={run.status} />
        <DetailRow label="Outcome" value={run.success === 'false' ? 'Completed with warnings' : 'Success'} />
        <DetailRow label="Pay period start" value={fmtDate(run.payPeriodStartDate)} />
        <DetailRow label="Pay period end" value={fmtDate(run.payPeriodEndDate)} />
        <DetailRow label="Payment date" value={fmtDate(run.paymentDate)} />
        <DetailRow label={hasQuickSuper ? 'Grand total super (Xero + QuickSuper)' : 'Total super'} value={fmtMoney(run.totalSuper)} bold />
        {run.finalisedAt && <DetailRow label="Finalised at" value={fmtDateTime(run.finalisedAt)} />}
        <DetailRow label="Organisation" value={run.tenantName ?? run.tenantId} />
        <DetailRow label="Payroll calendar" value={run.payrollCalendarName ?? '—'} />
        <DetailRow label="Calendar type" value={run.payrollCalendarType ?? '—'} />
        <DetailRow label="Source file" value={run.importFilename ?? '—'} />
        <DetailRow
          label="Report period"
          value={[run.reportMonth, run.reportYear].filter(Boolean).join(' ') || '—'}
        />
      </div>

      {run.xeroPayRunUrl && (
        <a
          href={run.xeroPayRunUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:text-emerald-800"
        >
          Open Xero Pay Runs
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
            <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
          </svg>
        </a>
      )}

      {lines.length > 0 && (
        <div className="space-y-1.5">
          {hasQuickSuper && <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Sent to Xero</p>}
          <XeroLinesTable lines={lines} />
          {hasQuickSuper && <SubtotalRow label="Xero subtotal" amount={sumSuper(lines)} />}
        </div>
      )}

      {hasQuickSuper && (
        <div className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Paid via AustralianSuper QuickSuper</p>
          <QuickSuperLinesTable lines={quickSuperLines} />
          <SubtotalRow label="QuickSuper subtotal" amount={sumSuper(quickSuperLines)} />
          <SubtotalRow label="Grand total" amount={Number(run.totalSuper)} bold />
        </div>
      )}
    </div>
  );
}

function sumSuper(lines: PastRunLineResult[]): number {
  return Math.round(lines.reduce((sum, l) => sum + Number(l.superAmount), 0) * 100) / 100;
}

function SubtotalRow({ label, amount, bold }: { label: string; amount: number; bold?: boolean }) {
  return (
    <div className="flex items-baseline justify-between rounded-md bg-emerald-50 px-3 py-2 text-xs">
      <span className={bold ? 'font-semibold text-slate-800' : 'text-slate-600'}>{label}</span>
      <span className="font-semibold text-emerald-800">{fmtMoney(String(amount))}</span>
    </div>
  );
}

function QuickSuperLinesTable({ lines }: { lines: PastRunLineResult[] }) {
  if (lines.length === 0) {
    return <p className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">No contractors were paid via QuickSuper.</p>;
  }
  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
      <table className="w-full text-left text-xs">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2 font-medium">Contractor</th>
            <th className="px-3 py-2 font-medium">QuickSuper member</th>
            <th className="px-3 py-2 font-medium text-right">Debit total</th>
            <th className="px-3 py-2 font-medium text-right">Super %</th>
            <th className="px-3 py-2 font-medium text-right">Super</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {lines.map((line, i) => (
            <tr key={`${line.contractorName}-${i}`}>
              <td className="px-3 py-2 text-slate-800">{line.contractorName}</td>
              <td className="px-3 py-2 text-slate-600">{line.quickSuperMemberName ?? '—'}</td>
              <td className="px-3 py-2 text-right text-slate-700">{fmtMoney(line.debitTotal)}</td>
              <td className="px-3 py-2 text-right text-slate-700">{line.superRate}%</td>
              <td className="px-3 py-2 text-right font-semibold text-slate-900">{fmtMoney(line.superAmount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function XeroLinesTable({ lines }: { lines: PastRunLineResult[] }) {
  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
      <table className="w-full text-left text-xs">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2 font-medium">Contractor</th>
            <th className="px-3 py-2 font-medium">Xero employee</th>
            <th className="px-3 py-2 font-medium text-right">Debit total</th>
            <th className="px-3 py-2 font-medium text-right">Super %</th>
            <th className="px-3 py-2 font-medium text-right">Super</th>
            <th className="px-3 py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {lines.map((line, i) => (
            <tr key={`${line.xeroEmployeeId ?? 'noid'}-${i}`}>
              <td className="px-3 py-2 text-slate-800">{line.contractorName}</td>
              <td className="px-3 py-2 text-slate-600">{line.xeroEmployeeName ?? '—'}</td>
              <td className="px-3 py-2 text-right text-slate-700">{fmtMoney(line.debitTotal)}</td>
              <td className="px-3 py-2 text-right text-slate-700">{line.superRate}%</td>
              <td className="px-3 py-2 text-right font-semibold text-slate-900">{fmtMoney(line.superAmount)}</td>
              <td className="px-3 py-2 text-slate-600">
                <span
                  className={[
                    'inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium',
                    line.status === 'success' || line.status === 'posted'
                      ? 'bg-emerald-100 text-emerald-700'
                      : line.status === 'error'
                      ? 'bg-rose-100 text-rose-700'
                      : 'bg-amber-100 text-amber-700',
                  ].join(' ')}
                >
                  {line.status}
                </span>
                {line.error && (
                  <p className="mt-1 text-[11px] text-rose-700">{line.error}</p>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
      
  );
}

function DetailRow({ label, value, mono, bold }: { label: string; value: string; mono?: boolean; bold?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="text-slate-500">{label}</span>
      <span
        className={[
          'text-right text-slate-800 truncate',
          mono ? 'font-mono text-[11px]' : '',
          bold ? 'font-semibold text-emerald-700' : 'font-medium',
        ].join(' ')}
      >
        {value}
      </span>
    </div>
  );
}

function DeleteModal({
  run,
  isSubmitting,
  errorMessage,
  onCancel,
  onConfirm,
}: {
  run: PastRun;
  isSubmitting: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-100 px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-500">
              <TrashIcon large />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-slate-900">Delete past run?</h2>
              <p className="mt-0.5 text-sm text-slate-500 truncate">
                {fmtDate(run.payPeriodStartDate)} – {fmtDate(run.payPeriodEndDate)}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3 px-6 py-5 text-sm text-slate-700">
          <p>
            This will remove the past run record from this app. The pay run inside Xero is{' '}
            <strong>not</strong> affected.
          </p>
          <p>This action cannot be undone.</p>

          {errorMessage && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {errorMessage}
            </div>
          )}
        </div>

        <div className="flex gap-3 border-t border-slate-100 px-6 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="flex-1 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-60"
          >
            {isSubmitting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm6.293-13.707a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 6.414V13a1 1 0 11-2 0V6.414L7.707 7.707a1 1 0 01-1.414-1.414l3-3z" clipRule="evenodd" transform="rotate(180 10 10)" />
      <path d="M4 14a1 1 0 011 1v1h10v-1a1 1 0 112 0v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2a1 1 0 011-1zM10 3a1 1 0 011 1v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L9 11.586V4a1 1 0 011-1z" />
    </svg>
  );
}

function PdfIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M5 2a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7.414a2 2 0 00-.586-1.414l-3.414-3.414A2 2 0 009.586 2H5zm5 1.5L13.5 7H11a1 1 0 01-1-1V3.5zM6 10a1 1 0 011-1h2a2.5 2.5 0 010 5H8v1a1 1 0 11-2 0v-5zm2 1v1h1a.5.5 0 000-1H8zm4-1a1 1 0 011-1h1.5a1 1 0 110 2H14v1h.5a1 1 0 110 2H14v1a1 1 0 11-2 0v-5z" />
    </svg>
  );
}

function TrashIcon({ large }: { large?: boolean } = {}) {
  const size = large ? 'h-5 w-5' : 'h-3.5 w-3.5';
  return (
    <svg className={size} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  );
}
